import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Star, ChevronLeft, ChevronRight, Sparkles, MapPin } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import Reveal from "../Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOOGLE_MAPS_URL_FALLBACK = "https://maps.app.goo.gl/yastikmedya";

const MAPS_LABELS = {
  tr: "Google'da Yorumları Gör",
  en: "See Reviews on Google",
  es: "Ver reseñas en Google",
  de: "Bewertungen auf Google",
  fr: "Voir sur Google Maps",
  ar: "عرض التقييمات",
};

export default function ReviewsSlider() {
  const { t, lang, isRTL } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [mapsUrl, setMapsUrl] = useState(GOOGLE_MAPS_URL_FALLBACK);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", direction: isRTL ? "rtl" : "ltr" },
    [Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })]
  );

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const fetchReviews = (attempt = 0) => {
      axios
        .get(`${API}/reviews`, { params: { lang } })
        .then((res) => {
          const data = res.data || [];
          if (!cancelled) {
            if (data.length === 0 && attempt < 3) {
              // Empty response — server may still be seeding, retry after 2s
              retryTimer = setTimeout(() => fetchReviews(attempt + 1), 2000);
            } else {
              setReviews(data);
            }
          }
        })
        .catch(() => { if (!cancelled) setReviews([]); });
    };

    fetchReviews();
    axios.get(`${API}/settings`, { timeout: 4000 })
      .then((r) => { if (!cancelled && r.data?.google_maps_reviews_url) setMapsUrl(r.data.google_maps_reviews_url); })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [lang]);

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, lang]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section
      data-testid="reviews-section"
      className="relative bg-cream py-section sm:py-section-lg border-t border-mist"
    >
      <div className="container-page">
        <Reveal as="div" className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <span className="eyebrow">
              <Sparkles className="h-3 w-3 text-gold" />
              {t("reviews.eyebrow")}
            </span>
            <h2 className="mt-5 font-display text-display-2 font-semibold text-ink max-w-2xl">
              {t("reviews.title_pre")}{" "}
              <span className="gold-foil-text">{t("reviews.title_accent")}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Google Maps reviews button */}
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-maps-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-ink/20 px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink hover:bg-ink hover:text-cream transition-colors"
            >
              <MapPin className="h-4 w-4" />
              {MAPS_LABELS[lang] || MAPS_LABELS.tr}
            </a>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={scrollPrev}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink hover:border-ink hover:bg-ink hover:text-cream transition-colors"
                aria-label={t("reviews.prev")}
                data-testid="reviews-prev"
              >
                <ChevronLeft className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />
              </button>
              <button
                type="button"
                onClick={scrollNext}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink hover:border-ink hover:bg-ink hover:text-cream transition-colors"
                aria-label={t("reviews.next")}
                data-testid="reviews-next"
              >
                <ChevronRight className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="overflow-hidden" ref={emblaRef} data-testid="reviews-carousel">
          <div className="flex gap-5">
            {(reviews.length ? reviews : Array.from({ length: 3 })).map((r, i) => (
              <article
                key={r?.id || `skel-${i}`}
                data-testid={`review-card-${i}`}
                className="flex-[0_0_88%] sm:flex-[0_0_60%] lg:flex-[0_0_38%] rounded-2xl border border-mist bg-white p-7 sm:p-8 flex flex-col"
              >
                <div className="flex items-center gap-1 text-gold">
                  {Array.from({ length: r?.rating || 5 }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <blockquote className="mt-5 font-display text-h3 leading-snug text-ink">
                  "{r?.text || ""}"
                </blockquote>
                <div className="mt-auto pt-7 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-ink">{r?.author || ""}</p>
                    <p className="text-xs text-ink/55">{r?.date || ""}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[0.65rem] font-medium uppercase tracking-[0.18em] text-ink/55 border border-mist rounded-full px-2.5 py-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    {r?.source || "Google"}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
